// src/services/api/invoiceAPI.js
import { createAuthHeaders } from '../../utils/authUtils';

const FIREBASE_FUNCTIONS_BASE_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL;

/**
 * Invoice API service for communication with Firebase functions
 */
export class InvoiceAPI {
  
  /**
   * Fetch invoices by date range from PostgreSQL
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of invoice objects
   */
  static async fetchInvoicesByDateRange(startDate, endDate) {
    try {
      console.log('InvoiceAPI: Fetching invoices by date range', {
        startDate,
        endDate
      });

      // Validate input parameters
      this.validateDateRange(startDate, endDate);

      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/invoices`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'getInvoicesByDateRange',
          startDate,
          endDate
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch invoices');
      }

      console.log('InvoiceAPI: Invoices fetched successfully', {
        count: result.data?.length || 0,
        dateRange: `${startDate} to ${endDate}`
      });

      return result.data || [];
      
    } catch (error) {
      console.error('InvoiceAPI: Error fetching invoices:', error);
      throw new Error(`Failed to fetch invoices: ${this.getErrorMessage(error)}`);
    }
  }

  /**
   * Validate date range parameters
   * @param {string} startDate - Start date
   * @param {string} endDate - End date
   */
  static validateDateRange(startDate, endDate) {
    if (!startDate || !endDate) {
      throw new Error('תאריכי התחלה וסיום נדרשים');
    }

    // Validate date format (basic check)
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (!datePattern.test(startDate) || !datePattern.test(endDate)) {
      throw new Error('פורמט תאריך לא תקין. יש להשתמש בפורמט YYYY-MM-DD');
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error('תאריכים לא תקינים');
    }

    if (start > end) {
      throw new Error('תאריך התחלה חייב להיות לפני תאריך הסיום');
    }

    // Check if range is not too large (max 1 year)
    const maxRange = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
    if (end - start > maxRange) {
      throw new Error('טווח התאריכים לא יכול להיות יותר משנה');
    }
  }

  /**
   * Get user-friendly error message
   * @param {Error} error - The error object
   * @returns {string} User-friendly error message in Hebrew
   */
  static getErrorMessage(error) {
    const message = error.message || '';

    // Network errors
    if (message.includes('Failed to fetch') || message.includes('network')) {
      return 'שגיאת רשת. בדוק את החיבור לאינטרנט ונסה שוב.';
    }

    // Authentication errors
    if (message.includes('unauthenticated') || message.includes('401')) {
      return 'נדרשת הזדהות מחדש. אנא התחבר שוב.';
    }

    // Permission errors
    if (message.includes('permission-denied') || message.includes('403')) {
      return 'אין הרשאה לצפייה בחשבוניות.';
    }

    // Server errors
    if (message.includes('unavailable') || message.includes('500')) {
      return 'השירות אינו זמין כרגע. נסה שוב מאוחר יותר.';
    }

    // Timeout errors
    if (message.includes('timeout') || message.includes('deadline-exceeded')) {
      return 'הבקשה ארכה יותר מדי. נסה שוב.';
    }

    // Return Hebrew error message if it exists, otherwise return generic message
    if (message.includes('תאריך') || message.includes('נדרש') || message.includes('טווח')) {
      return message;
    }

    return 'שגיאה בטעינת החשבוניות. אנא נסה שוב.';
  }

  /**
   * Retry mechanism for failed API calls
   * @param {Function} apiCall - The API call function to retry
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} delay - Delay between retries in milliseconds
   * @returns {Promise} Result of the API call
   */
  static async retryApiCall(apiCall, maxRetries = 3, delay = 1000) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`InvoiceAPI: Attempt ${attempt}/${maxRetries}`);
        const result = await apiCall();
        return result;
      } catch (error) {
        lastError = error;
        console.warn(`InvoiceAPI: Attempt ${attempt} failed:`, error.message);

        // Don't retry on validation errors (4xx status codes)
        if (error.message.includes('HTTP 4')) {
          throw error;
        }

        // Wait before retrying (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, delay * attempt));
        }
      }
    }

    throw lastError;
  }

  /**
   * Check if the API is healthy
   * @returns {Promise<boolean>} Whether the API is responsive
   */
  static async healthCheck() {
    try {
      const headers = await createAuthHeaders();
      
      const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/invoices`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          operation: 'healthCheck'
        })
      });

      return response.ok;
    } catch (error) {
      console.warn('InvoiceAPI: Health check failed:', error);
      return false;
    }
  }
}